/** @file The sync loop: one at a time; reloads the doc when asked, else fetches missed commits to the head. */
import { docStore } from '../../stores/docStore';
import { liveStore } from '../../stores/liveStore';
import { applyCommit } from './applyCommit';
import { hashCheck } from './hashCheck';
import { liveSession, type SyncSession } from './liveSession';

const isCurrent = (session: SyncSession): boolean => liveSession.get() === session && docStore.getState().projectId === session.projectId;

/** GET /doc: the new confirmed doc; own batches it does not carry stay pending; the epoch is bumped after the slice is written.
 *  The request stays wanted until the answer arrives, so a failed GET is retried by the next frame or pong. */
async function resync(session: SyncSession): Promise<void> {
  liveStore.setState({ status: 'resyncing' });
  const { doc, rev } = await session.client.getDoc(session.projectId);
  if (!isCurrent(session)) return;
  session.resyncWanted = false;
  const live = liveStore.getState();
  const batches = live.batches.filter((b) => b.committedRev === null || b.committedRev > rev);
  session.knownRev = Math.max(session.knownRev, rev);
  // The slice first: whoever reacts to the epoch bump (the feed reseed) must read the reloaded rev, not the old one.
  docStore.setState({ slice: { confirmed: doc, rev, pending: batches.flatMap((b) => b.pending) } });
  liveStore.setState({ batches, epoch: live.epoch + 1 });
}

/** One page of GET /changes after `rev`, applied in order; a gap or a page without patches asks for a resync. */
async function fetchPage(session: SyncSession, rev: number): Promise<void> {
  liveStore.setState({ status: 'catchingUp' });
  const page = await session.client.getChanges(session.projectId, rev, { patches: true });
  if (!isCurrent(session)) return;
  session.knownRev = page.changes.length === 0 ? Math.max(rev, page.currentRev) : Math.max(session.knownRev, page.currentRev);
  let at = rev;
  for (const [i, summary] of page.changes.entries()) {
    const patches = page.patches?.[i];
    if (summary.rev <= at) continue;
    if (summary.rev !== at + 1 || patches === undefined) {
      session.resyncWanted = true;
      return;
    }
    applyCommit(session, summary, patches);
    at = summary.rev;
  }
}

/** True when the loop ended with nothing left to do; false on a failure (the next frame or pong retries). */
async function loop(session: SyncSession): Promise<boolean> {
  try {
    while (isCurrent(session)) {
      if (session.resyncWanted) await resync(session);
      const rev = docStore.getState().slice?.rev;
      if (rev === undefined || (rev >= session.knownRev && !session.resyncWanted)) return true;
      if (!session.resyncWanted) await fetchPage(session, rev);
    }
    return false;
  } catch {
    return false;
  }
}

/** Starts the loop unless it runs (frames that arrive meanwhile only raise knownRev); afterwards flushes a due hash. */
export function kickSync(session: SyncSession): Promise<void> {
  if (session.busy !== null) return session.busy;
  const run = loop(session).then((ok) => {
    session.busy = null;
    if (!isCurrent(session)) return;
    liveStore.setState({ status: session.socketOpen ? 'live' : 'offline' });
    if (ok) hashCheck.flush(session);
  });
  session.busy = run;
  return run;
}
